import { Platform } from 'react-native';
import { clearSavedAddresses } from '@/utils/locationStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FAVORITES_KEY } from '@/store/favoritesKey';
import * as SecureStore from 'expo-secure-store';
import { API_BASE_URL, AUTH_BASE_URL } from '@/config/api';

const STORAGE_KEY = 'salunbd_customer_session_v1';

/**
 * Security guard: refuse to boot against an http:// URL in production. In dev the URL is the
 * plain-http dev-server host resolved in config/api.ts, so the engineer never has to spin up
 * TLS just to run it.
 */
if (!(process.env.NODE_ENV !== 'production')) {
  const insecure = [API_BASE_URL, AUTH_BASE_URL].filter((u) => u.startsWith('http://'));
  if (insecure.length > 0) {
    throw new Error(
      `Refusing to start: insecure API URL detected (${insecure.join(', ')}). ` +
        'Set EXPO_PUBLIC_API_URL (and EXPO_PUBLIC_AUTH_URL) to an https:// endpoint.',
    );
  }
}

let memoryStore: Record<string, string> = {};

const storage = {
  async getItem(key: string): Promise<string | null> {
    try {
      if (Platform.OS !== 'web') return await SecureStore.getItemAsync(key);
      if (typeof window !== 'undefined' && window.localStorage) return window.localStorage.getItem(key);
      return memoryStore[key] || null;
    } catch {
      return memoryStore[key] || null;
    }
  },
  async setItem(key: string, value: string): Promise<void> {
    try {
      if (Platform.OS !== 'web') {
        await SecureStore.setItemAsync(key, value);
        return;
      }
      if (typeof window !== 'undefined' && window.localStorage) window.localStorage.setItem(key, value);
      memoryStore[key] = value;
    } catch {
      memoryStore[key] = value;
    }
  },
  async removeItem(key: string): Promise<void> {
    try {
      if (Platform.OS !== 'web') {
        await SecureStore.deleteItemAsync(key);
        return;
      }
      if (typeof window !== 'undefined' && window.localStorage) window.localStorage.removeItem(key);
      delete memoryStore[key];
    } catch {
      delete memoryStore[key];
    }
  },
};

interface BackendUser {
  id: string;
  email: string;
  phone?: string;
  name: string;
  avatarUrl?: string;
  role: string;
  tenantId?: string;
}

interface BackendAuthResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: BackendUser;
}

/** Deliberately no longer 'typeof currentCustomer'. The session used to be built by spreading the
 *  mock customer and overlaying a few real fields, so every signed-in user carried the fixture's
 *  loyalty points, tier and 'Gulshan 1, Dhaka' as if they were their
 *  own — and no amount of updating their profile could change numbers that were never read from
 *  anywhere. These are now optional: absent means 'not loaded', which screens can handle, rather
 *  than a confident wrong number.
 *
 *  Loyalty has real endpoints already (loyaltyApi.getMyLoyalty);
 *  screens should read that rather than expect it here. */
export interface CustomerUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  role?: string;
  location?: string;
  loyaltyPoints?: number;
  lifetimePoints?: number;
  loyaltyTier?: 'Bronze' | 'Silver' | 'Gold';
  pointsToNextTier?: number;
  nextTier?: 'Silver' | 'Gold' | 'Platinum';
}

export interface Session {
  token: string;
  refreshToken: string;
  user: CustomerUser;
}

// The auth-service only knows about `User` (id/email/name/role/tenantId) — it has no concept of
// loyalty points (those live in service-payment). Real identity fields overlay
// onto the mock customer shape so every screen that already reads `state.auth.user` keeps working.
function toSession(auth: BackendAuthResponse): Session {
  const { user } = auth;
  const userRole = (user.role || '').toUpperCase();
  // Role separation guard: customer app ONLY accepts accounts with role 'CUSTOMER'
  if (userRole && userRole !== 'CUSTOMER') {
    const appDestination =
      userRole === 'STYLIST'
        ? 'the SalunBD Stylist app'
        : userRole === 'SUPER_ADMIN'
        ? 'the SalunBD Admin Console'
        : userRole === 'OWNER'
        ? 'the SalunBD Owner Dashboard'
        : 'the appropriate SalunBD app';
    throw new Error(`This account isn't a customer account (${user.role}). Please sign in from ${appDestination}.`);
  }

  return {
    token: auth.accessToken,
    refreshToken: auth.refreshToken,
    user: {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      // No mock fallback for either — showing a stray mock phone number or stock photo as if it
      // were the real signed-in user's own data is worse than just leaving it empty until they set
      // one for real (Avatar already renders initials when uri is falsy).
      phone: user.phone || '',
      avatar: user.avatarUrl || '',
    },
  };
}

async function authFetch<T = BackendAuthResponse>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${AUTH_BASE_URL}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = typeof body?.message === 'string' ? body.message : Array.isArray(body?.message) ? body.message[0] : `Request failed (${res.status})`;
    throw new Error(message);
  }
  return res.json();
}

async function authFetchVoid(path: string, init?: RequestInit): Promise<void> {
  const res = await fetch(`${AUTH_BASE_URL}${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = typeof body?.message === 'string' ? body.message : Array.isArray(body?.message) ? body.message[0] : `Request failed (${res.status})`;
    throw new Error(message);
  }
}

async function persistSession(session: Session): Promise<void> {
  await storage.setItem(STORAGE_KEY, JSON.stringify(session));
}

/** Reads whatever session was last persisted (if any) — used on app boot to restore a logged-in
 *  customer without making them sign in again every time they reopen the app. */
export async function loadSession(): Promise<Session | null> {
  const raw = await storage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as Session;
    // Discard any stale or non-customer session
    if (session?.user?.role && session.user.role.toUpperCase() !== 'CUSTOMER') {
      await storage.removeItem(STORAGE_KEY);
      return null;
    }
    return session;
  } catch {
    return null;
  }
}

export async function clearSession(): Promise<void> {
  await storage.removeItem(STORAGE_KEY);
  // Saved addresses belong to the account, not the phone.
  await clearSavedAddresses();
  // So are the saved favourites: the next person on this phone must not start from this account's list.
  await AsyncStorage.removeItem(FAVORITES_KEY).catch(() => undefined);
}

export interface LoginInput {
  email: string;
  password: string;
}

export async function login(input: LoginInput): Promise<Session> {
  // expectedRole tells the backend this login must resolve to a CUSTOMER account — even a correct
  // password on an OWNER/STYLIST/SUPER_ADMIN account is rejected server-side rather than
  // trusted client-side. See AuthService.login() on the backend.
  const auth = await authFetch(`/login`, { method: 'POST', body: JSON.stringify({ ...input, expectedRole: 'CUSTOMER' }) });
  const session = toSession(auth);
  await persistSession(session);
  return session;
}

/** "Continue with Google": the backend checks Google's ID token, then signs in the customer with
 *  that email or creates their account (no emailed code: Google already verified the address). */
export async function googleSignIn(idToken: string): Promise<Session> {
  const auth = await authFetch(`/google`, { method: 'POST', body: JSON.stringify({ idToken, app: 'CUSTOMER' }) });
  const session = toSession(auth);
  await persistSession(session);
  return session;
}

export interface RegisterInput {
  name: string;
  email: string;
  phone?: string;
  password: string;
}

export interface PendingRegistration {
  status: 'pending_verification';
  email: string;
  message: string;
}

// Registration doesn't create a usable account immediately — the backend holds the signup details
// and emails a 6-digit code; the account (and a real session) only exists once verifyEmail()
// confirms that code. See service-auth/src/auth/auth.service.ts.
export async function register(input: RegisterInput): Promise<PendingRegistration> {
  return authFetch<PendingRegistration>(`/register`, {
    method: 'POST',
    body: JSON.stringify({ ...input, role: 'CUSTOMER' }),
  });
}

export async function verifyEmail(email: string, code: string): Promise<Session> {
  const auth = await authFetch(`/verify-email`, { method: 'POST', body: JSON.stringify({ email, code }) });
  const session = toSession(auth);
  await persistSession(session);
  return session;
}

export async function resendVerification(email: string): Promise<void> {
  await authFetchVoid(`/resend-verification`, { method: 'POST', body: JSON.stringify({ email }) });
}

export async function forgotPassword(email: string): Promise<void> {
  await authFetchVoid(`/forgot-password`, { method: 'POST', body: JSON.stringify({ email }) });
}

/** Checks the emailed reset code before asking for a new password; does not use the code up. */
export async function verifyResetCode(email: string, code: string): Promise<void> {
  await authFetchVoid(`/verify-reset-code`, { method: 'POST', body: JSON.stringify({ email, code }) });
}

export async function resetPassword(email: string, code: string, newPassword: string): Promise<void> {
  await authFetchVoid(`/reset-password`, { method: 'POST', body: JSON.stringify({ email, code, newPassword }) });
}

export async function refreshSession(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
  const res = await fetch(`${AUTH_BASE_URL}/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) throw new Error('Session expired. Please log in again.');
  const data = (await res.json()) as { accessToken: string; refreshToken: string };

  const existing = await loadSession();
  if (existing) {
    await persistSession({ ...existing, token: data.accessToken, refreshToken: data.refreshToken });
  }
  return { token: data.accessToken, refreshToken: data.refreshToken };
}

// Current + new + confirm, no emailed code — the session says who you are and the current
// password confirms it's really you. The forgot-password flow (email + OTP) still exists
// separately for someone who's actually locked out.
export async function changePassword(
  token: string,
  currentPassword: string,
  newPassword: string,
): Promise<{ token: string; refreshToken: string }> {
  const res = await fetch(`${AUTH_BASE_URL}/change-password`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = typeof body?.message === 'string' ? body.message : `Request failed (${res.status})`;
    throw new Error(message);
  }
  const data = (await res.json()) as { accessToken: string; refreshToken: string };

  const existing = await loadSession();
  if (existing) {
    await persistSession({ ...existing, token: data.accessToken, refreshToken: data.refreshToken });
  }
  return { token: data.accessToken, refreshToken: data.refreshToken };
}

export async function logout(): Promise<void> {
  const session = await loadSession();
  if (session?.refreshToken) {
    await authFetchVoid(`/logout`, { method: 'POST', body: JSON.stringify({ refreshToken: session.refreshToken }) }).catch(() => {
      // Best-effort — still clear the local session even if the network call fails.
    });
  }
  await clearSession();
}

// Revokes every refresh token for this account (POST /auth/logout-all, requires the current
// access token) before clearing the local session — a real "sign out everywhere," not just this
// device. Was previously a UI-only action (Security Settings' "Log Out of All Devices" just
// cleared the local session, leaving every other device's session valid).
export async function logoutAll(): Promise<void> {
  const session = await loadSession();
  if (session?.token) {
    await authFetchVoid(`/logout-all`, {
      method: 'POST',
      headers: { authorization: `Bearer ${session.token}` },
    }).catch(() => {
      // Best-effort — still clear the local session even if the network call fails.
    });
  }
  await clearSession();
}

/** Verifies that the entered password is valid for the current account without altering the current session tokens. */
export async function verifyAccountPassword(email: string, password: string): Promise<boolean> {
  try {
    const auth = await authFetch<{ accessToken: string }>(`/login`, {
      method: 'POST',
      body: JSON.stringify({ email, password, expectedRole: 'CUSTOMER' }),
    });
    return !!auth?.accessToken;
  } catch {
    return false;
  }
}

